import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { portfolioData } from '../data/portfolioData';

const ProjectDetail = () => {
  const { id } = useParams();
  const project = portfolioData.find(p => p.id === id);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showFullNarrative, setShowFullNarrative] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const handleBackClick = () => {
    window.scrollTo(0, 0);
  };

  // Get current gallery images for the project
  const galleryImages = project?.galleryImages || [];

  // Find current image index
  const getCurrentImageIndex = () => {
    if (!selectedImage) return -1;
    return galleryImages.findIndex(img => img.url === selectedImage.url);
  };

  // Navigation functions
  const goToPrevious = () => {
    const currentIndex = getCurrentImageIndex();
    if (currentIndex > 0) {
      setSelectedImage(galleryImages[currentIndex - 1]);
    } else if (galleryImages.length > 0) {
      setSelectedImage(galleryImages[galleryImages.length - 1]);
    }
  };

  const goToNext = () => {
    const currentIndex = getCurrentImageIndex();
    if (currentIndex < galleryImages.length - 1) {
      setSelectedImage(galleryImages[currentIndex + 1]);
    } else if (galleryImages.length > 0) {
      setSelectedImage(galleryImages[0]);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedImage) return;

      if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      } else if (e.key === 'Escape') {
        closeLightbox();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedImage]);

  // Touch/swipe navigation
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrevious();
    }
  };

  const openLightbox = (image) => {
    setSelectedImage(image);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  };

  if (!project) {
    return (
      <section className="project-detail">
        <div className="container">
          <h2>Project Not Found</h2>
          <p>The project you're looking for doesn't exist.</p>
          <Link to="/portfolio" className="btn" onClick={handleBackClick}>Back to Portfolio</Link>
        </div>
      </section>
    );
  }

  return (
    <>
      {/* Project Hero Section */}
      <section className="project-hero">
        <div className="container">
          <h1>{project.title}</h1>
          <p>{project.description}</p>
        </div>
        {/* Hero background image */}
        <img
          src={project.image || project.detailImage || '/images/project-default.jpg'}
          alt={project.title}
          className="project-hero-bg"
        />
      </section>

      {/* Project Detail Section */}
      <section className="project-detail">
        <div className="container">
          <div className="project-content">
            <div className="project-text">
              <h2>About the Project</h2>
              <p>{project.fullDescription}</p>

              <div className="project-features">
                <h3>Key Features</h3>
                {project.features.map((feature, index) => (
                  <div key={index} className="project-feature">
                    <i className="fas fa-check-circle"></i>
                    <div>
                      <h4>{feature}</h4>
                    </div>
                  </div>
                ))}
              </div>

              {/* Feature Narrative Section */}
              {project.featureNarrative && (
                <div className="project-feature-narrative">
                  <h3>Comprehensive Capabilities</h3>
                  <div className={`narrative-content ${showFullNarrative ? 'expanded' : 'collapsed'}`}>
                    <p>{project.featureNarrative}</p>
                  </div>
                  {project.featureNarrative.length > 500 && (
                    <button 
                      className="btn-read-more"
                      onClick={() => setShowFullNarrative(!showFullNarrative)}
                    >
                      {showFullNarrative ? 'Show Less' : 'Read More'}
                      <i className={`fas fa-chevron-${showFullNarrative ? 'up' : 'down'}`}></i>
                    </button>
                  )}
                </div>
              )}

              {/* Web Dashboard Link */}
              {project.webDashboard && (
                <div className="project-web-dashboard">
                  <h3>Web Dashboard Access</h3>
                  <a 
                    href={project.webDashboard.url} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="dashboard-link"
                  >
                    <i className="fas fa-chart-line"></i>
                    <span>{project.webDashboard.description}</span>
                    <i className="fas fa-external-link-alt"></i>
                  </a>
                </div>
              )}

              <div className="tech-stack">
                <h3>Technology Stack</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '15px' }}>
                  {project.techStack.map((tech, index) => (
                    <span key={index} className="tech-tag">{tech}</span>
                  ))}
                </div>
              </div>

              {project.results && (
                <div className="project-results">
                  <h3>Results & Impact</h3>
                  <p>{project.results}</p>
                </div>
              )}
            </div>

            <div className="project-image-container">
              <div className="project-image-wrapper">
                <img
                  src={project.detailImage || project.image || '/images/project-default.jpg'}
                  alt={`${project.title} screenshot`}
                  className="project-image"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = '/images/project-default.jpg';
                  }}
                />
              </div>

              {/* App Store Badges */}
              {project.appLinks && project.appLinks.length > 0 && (
                <div className="app-store-badges">
                  {project.appLinks.map((link, index) => (
                    <a
                      key={index}
                      href={link.url}
                      className="store-link"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        src={link.platform === 'Android' ? '/images/googleplay.jpg' : '/images/appstore.jpg'}
                        alt={`Download on ${link.platform}`}
                        className="store-badge"
                      />
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Image Gallery Section - Enhanced UI */}
          {galleryImages.length > 0 && (
            <div className="project-gallery">
              <h2>Project Gallery</h2>
              <p className="gallery-subtitle">View screenshots and images from the {project.title}</p>
              
              <div className="gallery-grid">
                {galleryImages.map((image, index) => (
                  <div 
                    key={index} 
                    className="gallery-item"
                    onClick={() => openLightbox(image)}
                  >
                    <div className="gallery-image-wrapper">
                      <img 
                        src={image.url} 
                        alt={image.caption || `${project.title} screenshot ${index + 1}`} 
                        className="gallery-image"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = '/images/placeholder.jpg';
                        }}
                      />
                    </div>
                    {image.caption && (
                      <div className="gallery-caption">
                        <h4>{image.caption}</h4>
                        {image.description && <p>{image.description}</p>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="back-button-container">
            <Link to="/portfolio" className="btn btn-secondary" onClick={handleBackClick}>
              <i className="fas fa-arrow-left"></i> Back to Portfolio
            </Link>
          </div>
        </div>
      </section>

      {/* Enhanced Lightbox Modal with Navigation */}
      {selectedImage && (
        <div 
          className="lightbox-modal" 
          onClick={closeLightbox}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox}>
              <i className="fas fa-times"></i>
            </button>

            {/* Previous Button */}
            {galleryImages.length > 1 && (
              <button className="lightbox-nav prev" onClick={goToPrevious}>
                <i className="fas fa-chevron-left"></i>
              </button>
            )}

            {/* Next Button */}
            {galleryImages.length > 1 && (
              <button className="lightbox-nav next" onClick={goToNext}>
                <i className="fas fa-chevron-right"></i>
              </button>
            )}

            <div className="lightbox-image-wrapper">
              <img
                src={selectedImage.url}
                alt={selectedImage.caption || project.title}
                className="lightbox-image"
              />
            </div>

            {/* Image counter */}
            {galleryImages.length > 1 && (
              <div className="lightbox-counter">
                {getCurrentImageIndex() + 1} / {galleryImages.length}
              </div>
            )}

            {(selectedImage.caption || selectedImage.description) && (
              <div className="lightbox-caption">
                <h3>{selectedImage.caption}</h3>
                <p>{selectedImage.description}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ProjectDetail;